import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { contractorApi } from '../../lib/contractorApi';

interface Message {
  id: string;
  job_id: string;
  sender_type: 'contractor' | 'homeowner';
  sender_id: string;
  message_text: string | null;
  photo_r2_key: string | null;
  photo_url: string | null;
  message_type: string;
  read_at: string | null;
  created_at: string;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (now.getTime() - d.getTime() < 24 * 3600 * 1000) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function JobMessaging() {
  const { jobId } = useParams<{ jobId: string }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!jobId) return;
    try {
      const data = await contractorApi.get<{ data: Message[] }>(`/contractor/jobs/${jobId}/messages`);
      setMessages(data.data ?? []);
    } catch {
      // non-fatal for poll
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 30_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!jobId || messages.length === 0) return;
    const unread = messages.filter(m => m.sender_type === 'homeowner' && !m.read_at);
    for (const m of unread) {
      contractorApi.put<unknown>(`/contractor/jobs/${jobId}/messages/${m.id}/read`, {}).catch(() => {});
    }
  }, [jobId, messages]);

  async function sendMessage() {
    if (!text.trim() || !jobId) return;
    setSending(true);
    setError(null);
    try {
      const data = await contractorApi.post<{ data: Message }>(`/contractor/jobs/${jobId}/messages`, {
        message_text: text.trim(),
        message_type: 'text',
      });
      setMessages(prev => [...prev, data.data]);
      setText('');
    } catch {
      setError('Failed to send message. Try again.');
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-4rem)] max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-10">
        <Link to="/app/contractor/jobs" className="text-gray-500 hover:text-gray-800">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-base font-semibold text-gray-900">Job Messages</h1>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
        {messages.length === 0 && (
          <p className="text-center text-sm text-gray-500 py-12">No messages yet. Start the conversation.</p>
        )}

        {messages.map(msg => {
          const isMine = msg.sender_type === 'contractor';
          const isSystem = msg.message_type === 'system';

          if (isSystem) {
            return (
              <div key={msg.id} className="flex justify-center">
                <span className="text-xs text-gray-400 bg-white border border-gray-200 rounded-full px-3 py-1">
                  {msg.message_text}
                </span>
              </div>
            );
          }

          return (
            <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm ${
                isMine
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm'
              }`}>
                {msg.photo_url && (
                  <img src={msg.photo_url} alt="Attached photo" className="rounded-lg mb-2 max-w-full" />
                )}
                {msg.message_text && (
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.message_text}</p>
                )}
                <p className={`text-[10px] mt-1 ${isMine ? 'text-blue-200' : 'text-gray-400'}`}>
                  {formatTime(msg.created_at)}
                  {isMine && msg.read_at && ' · Read'}
                </p>
              </div>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 pb-2">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-gray-200 bg-white px-4 py-3 flex gap-2 items-end">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message…"
          rows={1}
          className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent max-h-32 overflow-y-auto"
          style={{ minHeight: '40px' }}
        />
        <button
          onClick={sendMessage}
          disabled={sending || !text.trim()}
          className="shrink-0 w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center disabled:opacity-40 hover:bg-blue-700 transition-colors"
          aria-label="Send message"
        >
          {sending
            ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <svg className="w-5 h-5 rotate-90" fill="currentColor" viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
          }
        </button>
      </div>
    </div>
  );
}
